import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { api } from '../../lib/api'
import { notify } from '../../lib/notify'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select } from '../ui/select'
import { SidePanel } from '../ui/side-panel'

/**
 * Chat-Anlage (1:1 oder Gruppe). Aus chat.tsx extrahiert (Hub Paket 4),
 * damit der Projekt-Hub denselben Dialog mit vorbelegtem Projekt öffnen
 * kann. Projekt-Zuordnung gibt es nur für Gruppen-Chats — der Server
 * lehnt Direkt-Chats mit projectId ab.
 */
export function NewChatDialog({
  myId,
  onClose,
  onCreated,
  presetProjectId,
  presetName,
}: {
  myId: string
  onClose: () => void
  onCreated: (chatId: string) => void
  /** Hub: Projekt fest vorbelegen (Selector ausgeblendet). */
  presetProjectId?: string
  presetName?: string
}) {
  const { data: usersData } = useQuery({
    queryKey: ['documents', 'sendable-users'],
    queryFn: () => api.documentSendableUsers(),
  })
  const users = (usersData?.users ?? []).filter((u) => u.id !== myId)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [name, setName] = useState(presetName ?? '')
  const [search, setSearch] = useState('')
  const [projectId, setProjectId] = useState(presetProjectId ?? '')

  // Projekt-Pool nur laden, wenn der Selector sichtbar ist (kein Preset).
  const { data: projectsData } = useQuery({
    queryKey: ['projects', { archived: false }],
    queryFn: () => api.listProjects({ archived: false }),
    enabled: !presetProjectId,
  })
  const projects = projectsData?.projects ?? []

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return users
    return users.filter(
      (u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    )
  }, [users, search])

  const toggle = (id: string) => {
    setSelected((p) => {
      const n = new Set(p)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  }

  // Mit Projekt-Vorbelegung ist auch ein 2-Personen-Chat eine benannte
  // Gruppe (Projekt-Chats sind Gruppen-Chats).
  const isGroup = selected.size > 1 || (!!projectId && selected.size >= 1)
  const needsName = isGroup && !name.trim()

  const create = useMutation({
    mutationFn: () =>
      api.createChat({
        memberIds: Array.from(selected),
        name: isGroup ? name.trim() : undefined,
        projectId: isGroup && projectId ? projectId : undefined,
      }),
    onSuccess: (r) => onCreated(r.chat.id),
    onError: (err: Error) => notify.error(err.message),
  })

  return (
    <SidePanel
      open
      onClose={onClose}
      title={isGroup ? 'Neuer Gruppen-Chat' : 'Neuer Chat'}
      description={
        presetProjectId
          ? 'Projekt-Chat — Teilnehmer wählen und Name vergeben.'
          : isGroup
            ? `${selected.size} Teilnehmer ausgewählt — gib der Gruppe einen Namen.`
            : 'Wähle einen Mitarbeiter für einen 1-zu-1-Chat oder mehrere für eine Gruppe.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button
            disabled={selected.size === 0 || needsName || create.isPending}
            onClick={() => create.mutate()}
          >
            {isGroup ? 'Gruppe anlegen' : 'Chat öffnen'}
          </Button>
        </>
      }
    >
      <div className="space-y-4 pb-2">
        {isGroup && (
          <div className="space-y-2">
            <Label htmlFor="nc-name">Gruppen-Name</Label>
            <Input
              id="nc-name"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 120))}
              placeholder="z.B. Sprint-Planning"
            />
          </div>
        )}
        {!presetProjectId && (
          <div className="space-y-2">
            <Label htmlFor="nc-project">Projekt (optional, macht den Chat zur Gruppe)</Label>
            <Select
              id="nc-project"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <option value="">— Kein Projekt —</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="nc-search">Teilnehmer ({selected.size})</Label>
          <Input
            id="nc-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen…"
          />
          <div className="max-h-64 overflow-y-auto rounded-lg border border-border-subtle divide-y divide-border-subtle">
            {filtered.length === 0 ? (
              <p className="p-3 text-sm text-text-tertiary">Keine Treffer.</p>
            ) : (
              filtered.map((u) => {
                const checked = selected.has(u.id)
                return (
                  <label
                    key={u.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-bg-overlay/40 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(u.id)}
                      className="accent-accent h-4 w-4"
                    />
                    <span className="text-sm text-text-primary flex-1 min-w-0 truncate">
                      {u.name}
                    </span>
                    <span className="text-xs text-text-tertiary shrink-0">{u.email}</span>
                  </label>
                )
              })
            )}
          </div>
        </div>
      </div>
    </SidePanel>
  )
}
